import {
  fetch_manual_verification_question_mappings,
  fetch_manual_verification_run,
  fetch_manual_verification_runs
} from '@/services/onboarding_journey_service';
import { fetch_form_structure } from '@/services/main_section_service';
import { normalize_main_section } from '@/services/onboarding_form_helpers';
import { build_lightweight_case_checks } from './manage_verification_load_helpers';

export const get_manual_binding_form_link_id = (binding = {}) => Number(
  binding?.manual_form_link
  || binding?.manual_form_link_id
  || binding?.form_link
  || binding?.form_link_id
  || 0
) || null;

export const get_run_manual_form_link_id = (run = {}) => Number(
  run?.manual_form_link
  || run?.manual_form_link_id
  || run?.form_link
  || run?.form_link_id
  || 0
) || null;

export const get_item_manual_form_link_id = (item = {}) => Number(
  get_manual_binding_form_link_id(item?.manual_binding)
  || item?.manual_form_link
  || item?.manual_form_link_id
  || item?.form_link
  || item?.form_link_id
  || 0
) || null;

const get_case_runtime_key = (case_item = {}) => String(
  case_item?.application_id || case_item?.id || case_item?.entity_profile_id || ''
);

const run_matches_case = (run = {}, case_item = {}) => {
  const run_application_id = Number(run?.application || run?.application_id || 0);
  const run_entity_profile_id = Number(run?.entity_profile || run?.entity_profile_id || 0);
  const case_application_id = Number(case_item?.application_id || case_item?.id || 0);
  const case_entity_profile_id = Number(case_item?.entity_profile_id || case_item?.profile?.id || 0);

  return (
    (run_application_id > 0 && run_application_id === case_application_id)
    || (run_entity_profile_id > 0 && run_entity_profile_id === case_entity_profile_id)
  );
};

const normalize_manual_verification_type = (value = '') => String(value || '').trim().toLowerCase();

const is_bank_statement_run = (run = {}) => (
  normalize_manual_verification_type(run?.verification_type) === 'bank_statement'
);

const normalize_bank_statement_status = (value = '') => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[_-]+/g, ' ');

const get_run_bank_statement_verification = (run = {}) => (
  run?.bank_statement_verification || null
);

const get_run_bank_statement_status = (run = {}) => {
  const verification = get_run_bank_statement_verification(run);
  return normalize_bank_statement_status(
    verification?.current_status
    || verification?.status
    || verification?.current_attempt?.status
    || ''
  );
};

const get_bank_statement_placeholder_status = (run = {}) => {
  const status = get_run_bank_statement_status(run);
  if (status === 'completed') return 'Completed';
  if (status === 'processing') return 'Processing';
  if (status === 'queued') return 'Queued';
  if (status === 'failed') return 'Failed';
  if (status === 'rejected') return 'Rejected';
  return run?.status || 'Pending';
};

const get_bank_statement_placeholder_tone = (run = {}) => {
  const status = get_run_bank_statement_status(run);
  if (status === 'completed') return 'success';
  if (['failed', 'rejected'].includes(status)) return 'danger';
  if (['queued', 'processing'].includes(status)) return 'warning';
  return 'warning';
};

const get_manual_run_rank = (run = {}) => {
  const run_id = Number(run?.id || run?.run_id || 0);
  if (run_id > 0) return run_id;

  const timestamp = Date.parse(String(
    run?.started_at
    || run?.completed_at
    || run?.created_at
    || ''
  ));
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const pick_latest_manual_run = (runs = []) => {
  const normalized_runs = Array.isArray(runs) ? runs : [];
  if (!normalized_runs.length) return {};

  return normalized_runs.reduce((latest_run, run) => (
    get_manual_run_rank(run) >= get_manual_run_rank(latest_run)
      ? run
      : latest_run
  ), normalized_runs[0] || {});
};

const find_manual_run_for_case = (manual_runs = [], case_item = {}) => {
  const matched_runs = (Array.isArray(manual_runs) ? manual_runs : [])
    .filter((run) => run_matches_case(run, case_item));
  if (!matched_runs.length) return {};

  const bank_statement_runs = matched_runs.filter((run) => is_bank_statement_run(run));
  return pick_latest_manual_run(bank_statement_runs.length ? bank_statement_runs : matched_runs);
};

function get_case_application_id(case_item = {}) {
  return Number(case_item?.application_id || case_item?.id || 0);
}

function get_case_entity_profile_id(case_item = {}) {
  return Number(case_item?.entity_profile_id || case_item?.profile?.id || 0);
}

function get_case_manual_run_params(case_item = {}) {
  const params = { only_latest_cycle: true };
  const application_id = get_case_application_id(case_item);
  const entity_profile_id = get_case_entity_profile_id(case_item);
  if (application_id > 0) params.application_id = application_id;
  if (entity_profile_id > 0) params.entity_profile_id = entity_profile_id;
  return params;
}

async function fetch_manual_runs_for_case(case_item = {}) {
  const params = get_case_manual_run_params(case_item);
  if (!params.application_id && !params.entity_profile_id) return [];

  return fetch_manual_verification_runs(params).catch(() => []);
}

const build_manual_runtime_placeholder = (case_item = {}, run = {}) => ({
  id: `manual-verification-runtime-${get_case_runtime_key(case_item)}`,
  check: run?.verification_type || 'Manual verification gate',
  case_name: case_item?.name || 'Onboarded Entity',
  source: 'Manual verification',
  status: is_bank_statement_run(run)
    ? get_bank_statement_placeholder_status(run)
    : (run?.status || 'Pending'),
  status_tone: is_bank_statement_run(run)
    ? get_bank_statement_placeholder_tone(run)
    : 'warning',
  impact: 'Needs manual review',
  last_run: run?.started_at || '-',
  application_form_id: null,
  service_id: null,
  verification_mode: 'manual',
  is_gate_placeholder: false,
  is_runnable: false,
  verification_type: run?.verification_type || 'manual_verification',
  run_id: run?.id || run?.run_id || null,
  manual_run: run,
  bank_statement_verification: get_run_bank_statement_verification(run),
  manual_form_link: run?.manual_form_link || run?.manual_form_link_id || null,
  form_link: run?.form_link || run?.form_link_id || null,
  manual_verification_survey_form_id: run?.manual_verification_survey_form_id || null,
  manual_verification_form_name: run?.manual_verification_form_name || '',
  manual_binding: {
    id: run?.binding || run?.binding_id || null,
    manual_form_link: run?.manual_form_link || run?.manual_form_link_id || null,
    onboarding_form_link: run?.form_link || run?.form_link_id || null,
    manual_verification_survey_form_id: run?.manual_verification_survey_form_id || null,
    manual_verification_form_name: run?.manual_verification_form_name || '',
    verification_type: run?.verification_type || 'manual_verification',
    is_active: true
  },
  case_item
});

export const build_manual_runtime_checks_for_cases = async (cases = []) => {
  const placeholders = await Promise.all(cases.map(async (case_item) => {
    const manual_runs = await fetch_manual_runs_for_case(case_item);
    const run = find_manual_run_for_case(manual_runs, case_item);
    return run_matches_case(run, case_item)
      ? build_manual_runtime_placeholder(case_item, run)
      : null;
  }));

  return placeholders.filter(Boolean);
};

export async function build_initial_case_checks(cases = [], is_manual_page = false, options = {}) {
  const workspace_mode = typeof is_manual_page === 'string'
    ? is_manual_page
    : (is_manual_page ? 'manual' : 'verification');
  const stage_checks = build_lightweight_case_checks(cases, workspace_mode);
  const include_runtime_checks = options?.include_runtime_checks === true;
  if (workspace_mode === 'verification') return stage_checks;
  if (!include_runtime_checks) return stage_checks;

  const runtime_checks = await build_manual_runtime_checks_for_cases(cases);
  const runtime_keys = new Set(runtime_checks.map((check) => get_case_runtime_key(check?.case_item)));
  const stage_checks_without_runtime = stage_checks.filter(
    (check) => !runtime_keys.has(get_case_runtime_key(check?.case_item))
  );
  return [...stage_checks_without_runtime, ...runtime_checks];
}

export const build_manual_mapping_by_link = async (manual_bindings = []) => {
  const link_ids = [...new Set(
    (Array.isArray(manual_bindings) ? manual_bindings : [])
      .map(get_manual_binding_form_link_id)
      .filter((link_id) => Number(link_id || 0) > 0)
  )];
  const mapping_pairs = await Promise.all(
    link_ids.map(async (link_id) => [
      String(link_id),
      await fetch_manual_verification_question_mappings(link_id).catch(() => [])
    ])
  );
  return Object.fromEntries(mapping_pairs);
};

const get_manual_survey_form_id = (check = {}) => Number(
  check?.manual_binding?.manual_verification_survey_form_id
  || check?.manual_verification_survey_form_id
  || 0
) || null;

const build_manual_mapping_from_question = (question = {}) => ({
  id: `manual-form-question-${question?.id || question?.question_id || 'field'}`,
  form_link: null,
  onboarding_section_question: null,
  onboarding_question_text: '',
  manual_section_question: Number(question?.id || 0) || null,
  manual_question_text: question?.label || 'Manual verification question',
  is_active: true
});

const build_manual_form_question = (question = {}) => ({
  id: Number(question?.id || 0) || null,
  label: question?.label || 'Manual verification question',
  answer: '',
  mapping: build_manual_mapping_from_question(question),
  source_label: ''
});

const build_manual_form_sub_section = (sub_section = {}) => ({
  id: sub_section?.id || sub_section?.label || 'manual-review-section',
  label: sub_section?.label || 'Review questions',
  questions: (Array.isArray(sub_section?.questions) ? sub_section.questions : [])
    .filter((question) => Number(question?.id || 0) > 0)
    .map(build_manual_form_question)
});

const build_manual_form_section = (main_section = {}) => ({
  id: main_section?.id || main_section?.label || 'manual-review-form-section',
  label: main_section?.label || 'Manual verification',
  sub_sections: (Array.isArray(main_section?.sub_sections) ? main_section.sub_sections : [])
    .map(build_manual_form_sub_section)
    .filter((sub_section) => sub_section.questions.length)
});

const normalize_form_structure = (items = []) => (
  (Array.isArray(items) ? items : [])
    .map((main_section, index) => normalize_main_section(main_section, index))
);

const build_manual_form_sections_from_structure = (items = []) => (
  normalize_form_structure(items)
    .map(build_manual_form_section)
    .filter((main_section) => main_section.sub_sections.length)
);

const build_manual_mappings_from_form_structure = (items = []) => (
  normalize_form_structure(items)
    .flatMap((main_section) => (
      main_section.sub_sections.flatMap((sub_section) => (
        sub_section.questions
          .filter((question) => Number(question?.id || 0) > 0)
          .map(build_manual_mapping_from_question)
      ))
    ))
);

const fetch_manual_form_question_mappings = async (check = {}) => {
  const survey_form_id = get_manual_survey_form_id(check);
  if (!survey_form_id) return {};

  const form_structure = await fetch_form_structure(survey_form_id).catch(() => []);
  return {
    manual_mappings: build_manual_mappings_from_form_structure(form_structure),
    manual_form_sections: build_manual_form_sections_from_structure(form_structure)
  };
};

const enrich_manual_check_with_form_questions = async (check = {}) => {
  if (Array.isArray(check?.manual_mappings) && check.manual_mappings.length) return check;

  const form_payload = await fetch_manual_form_question_mappings(check);
  const manual_mappings = form_payload.manual_mappings || [];
  if (!manual_mappings.length) return check;

  return {
    ...check,
    check: check?.manual_binding?.manual_verification_form_name || check?.check,
    source: check?.manual_binding?.manual_verification_form_name || check?.source,
    manual_mappings,
    manual_form_sections: form_payload.manual_form_sections || []
  };
};

const fetch_run_manual_mappings = async (run = {}, check = {}) => {
  const manual_form_link_id = get_run_manual_form_link_id(run)
    || get_manual_binding_form_link_id(check?.manual_binding);
  if (!manual_form_link_id) return [];

  return fetch_manual_verification_question_mappings(manual_form_link_id)
    .catch(() => []);
};

const manual_check_can_lookup_mappings = (check = {}) => Boolean(
  Number(check?.run_id || 0)
  || get_manual_binding_form_link_id(check?.manual_binding)
  || get_run_manual_form_link_id(check?.manual_run)
);

const enrich_manual_check_with_run = async (check = {}) => {
  const run_id = Number(check?.run_id || 0);
  const has_mappings = Array.isArray(check?.manual_mappings) && check.manual_mappings.length;
  if (has_mappings) return check;
  if (!manual_check_can_lookup_mappings(check)) return enrich_manual_check_with_form_questions(check);

  const run = run_id
    ? await fetch_manual_verification_run(run_id).catch(() => null)
    : check?.manual_run || {};
  if (!run && !check?.manual_binding) return enrich_manual_check_with_form_questions(check);

  const enriched_check = {
    ...check,
    bank_statement_verification: run?.bank_statement_verification
      || check?.bank_statement_verification
      || null,
    manual_run: {
      ...check.manual_run,
      ...run,
      bank_statement_verification: run?.bank_statement_verification
        || check?.manual_run?.bank_statement_verification
        || check?.bank_statement_verification
        || null
    },
    field_runs: Array.isArray(run?.field_runs) ? run.field_runs : check.field_runs,
    manual_mappings: await fetch_run_manual_mappings(run || {}, check)
  };

  return enrich_manual_check_with_form_questions(enriched_check);
};

export const enrich_manual_checks_with_run_mappings = async (checks = []) => Promise.all(
  checks.map((check) => (
    check?.verification_mode === 'manual'
      ? enrich_manual_check_with_run(check)
      : Promise.resolve(check)
  ))
);

export const check_has_manual_mappings = (check = {}) => (
  Array.isArray(check?.manual_mappings) && check.manual_mappings.length > 0
);

const get_case_manual_bindings = (case_item = {}) => (
  Array.isArray(case_item?.manual_verification_bindings)
    ? case_item.manual_verification_bindings
    : []
);

export const apply_case_manual_binding_fallback = (binding_data = {}, case_item = {}) => {
  const manual_bindings = Array.isArray(binding_data?.manual_bindings)
    ? binding_data.manual_bindings
    : [];
  if (manual_bindings.length) return binding_data;

  const fallback_bindings = get_case_manual_bindings(case_item);
  if (!fallback_bindings.length) return binding_data;

  return {
    ...binding_data,
    manual_bindings: fallback_bindings
  };
};
